import { useEffect, useRef } from "react";
import { getMetersFromPixels } from "../../../../functions/helper/helpers.functions.js";
import { setFeatures } from "../functions/drawer.map.js";
import { along, length, inside } from "@turf/turf";
import { v1 } from "uuid";
import {
  arc,
  pulsingDot,
} from "../../../../assets/images/map/staticIcons/pulsingDot.js";
import { map } from "./useMap.sideEffects.js";
import { CAMPUS_COLLECTION } from "../../../../reduxStore/actions/main.js";
import Toast from "../../../../functions/classes/toast.class.js";
import { pageNames } from "../../../../reduxStore/reducers/main.js";
import useIntl from "react-intl/lib/src/components/useIntl.js";
import { getNodeById } from "../../../../functions/classes/node.class.js";
import { sourceIds } from "../variables/sourceIds.js";
import { layerIds } from "../variables/layerIds.js";
import UserLocation from "../../../../functions/classes/location.class.js";
import { CUSTOMIZABLE_VALUES } from "../variables/CUSTOMIZABLE_VALUES.js";

/**
 * Position des User udpaten, sowie das Heading(oder Bearing) und die Accuracy des Signals
 * @param mapLoaded
 * @param serverDataLoaded
 * @param userAllowedGPS
 * @param {UserLocation}locationObject
 * @param hasLatestPosition
 * @param currentMainPage
 * @param addToast
 * @param currentFloorIndex
 * @param cameraMoveToHeading
 * @param isTerminal
 * @param defaultStartNode
 * @param setLocationObjectZIndex
 * @param setCurrentFloorNumber
 * @param setCurrentFloorIndex
 */
export default function usePosition({
  mapLoaded,
  serverDataLoaded,
  userAllowedGPS,
  locationObject,
  hasLatestPosition,
  currentMainPage,
  addToast,
  currentFloorIndex,
  cameraMoveToHeading,
  isTerminal,
  defaultStartNode,
  setLocationObjectZIndex,
  setCurrentFloorIndex,
  currentFeature,
  currentRoute,
}) {
  const intl = useIntl();
  const canResetUserPosition = !isTerminal;
  // diese Tokens werden mitgeschickt, damit die animationen wissen wann sie stoppen sollen
  const animationTokens = useRef();
  const animationTime =
    locationObject.animationTime != null ? locationObject.animationTime : 60;

  // --- Refs ---
  const lastStep = useRef([]);
  const animationLoopCounter = useRef(0);
  const routeCollection = useRef({
    type: "FeatureCollection",
    features: [
      {
        id: 0,
        type: "Feature",
        properties: {},
        geometry: {
          type: "Point",
        },
      },
    ],
  });
  const pointCollection = useRef({
    type: "FeatureCollection",
    features: [
      {
        id: 0,
        type: "Feature",
        properties: {},
        geometry: {
          type: "Point",
          coordinates: [],
        },
      },
    ],
  });
  const initZIndex = useRef(true);

  // Einfärbung der momentanen Position
  const setPositionColor = (positionAvailable, dotcolor, halocolor) => {
    let color;
    let halo;
    if (positionAvailable) {
      // POSITION VERFÜGBAR
      pulsingDot.setColor(CUSTOMIZABLE_VALUES.lineFill);
      color = dotcolor ? dotcolor : CUSTOMIZABLE_VALUES.userPuck;
      halo = halocolor ? halocolor : CUSTOMIZABLE_VALUES.userPuck_radar;
      if (+locationObject.zIndex === +currentFloorIndex) {
        // User Position hat den gleichen zIndex wie Stockwerk
        // oder user nutzt gps, da dieses keine ahnung vom zIndex hat!
        arc.setOpacity("99");
        map.setPaintProperty(layerIds.nodes.position.dot, "circle-opacity", 1);
        map.setPaintProperty(
          layerIds.nodes.position.radar,
          "circle-opacity",
          0.15
        );
      } else {
        // User Position ist auf einem anderen Stockwerk
        arc.setOpacity("33");
        map.setPaintProperty(
          layerIds.nodes.position.dot,
          "circle-opacity",
          0.3
        );
        map.setPaintProperty(
          layerIds.nodes.position.radar,
          "circle-opacity",
          0.1
        );
      }

      // falls kein Heading verfügbar ist, darf man es nicht sichtbar machen!
      if (
        map.getLayer(layerIds.nodes.position.arc) &&
        locationObject.heading != null
      )
        map.setLayoutProperty(
          layerIds.nodes.position.arc,
          "visibility",
          "visible"
        );
    } else {
      // POSITION NICHT MEHR AKTUELL
      color = CUSTOMIZABLE_VALUES.lineHalo_inactive;
      halo = CUSTOMIZABLE_VALUES.lineHalo_inactive;
      if (map.getLayer(layerIds.nodes.position.arc)) {
        map.setLayoutProperty(
          layerIds.nodes.position.arc,
          "visibility",
          "none"
        );
      }
    }

    map.setPaintProperty(layerIds.nodes.position.radar, "circle-color", color);
    map.setPaintProperty(
      layerIds.nodes.position.radar,
      "circle-stroke-color",
      halo
    );
  };

  // Lediglich die Daten setzen, falls welche da sind oder keine um den Positionspuk verschwinden zu lassen
  const setPositionFeature = (lngLat) => {
    setFeatures({
      sourceId: sourceIds.position,
      features: lngLat
        ? [
            {
              type: "Feature",
              geometry: {
                type: "Point",
                coordinates: lngLat,
              },
            },
          ]
        : [],
    });
  };

  // Animation für Positionswechsel
  const animate = (token) => {
    if (token !== animationTokens.current) {
      // überprüfen ob übergebener token noch vorhanden ist
      // beende wenn bereits eine neue animation gestartet hat!
      return;
    }

    if (animationLoopCounter.current % 2 === 0) {
      // setz bitte nur bei jeder zweiten Koordinate die Position in mapbox neu
      // setData ist nämlich etwas ressourcen intense, deshalb sparen hier!
      const coordinates =
        routeCollection.current.features[0].geometry.coordinates[
          // diese Rechnung sorgt dafür, dass die indexierung nicht immer 3 voranspringt, sondern
          // nur eines (obwohl diese funktion nur jedes dritte mal aufgerufen wird)
          // wo wird jedes segment von der Line genommen!
          animationLoopCounter.current
        ];

      pointCollection.current.features[0].geometry.coordinates = coordinates;

      if (coordinates) {
        setFeatures({
          sourceId: sourceIds.position,
          features: pointCollection.current,
        });

        // AktuellePosition zum lastStep machen
        lastStep.current = coordinates;
      }
    }

    // Request the next frame of animation as long as the end has not been reached
    if (animationLoopCounter.current < animationTime) {
      animationLoopCounter.current++;
      requestAnimationFrame(() => animate(token));
    }
  };
  const setArc = () => {
    if (
      !serverDataLoaded ||
      userAllowedGPS == null ||
      !mapLoaded ||
      locationObject.heading == null
    )
      return;

    // richtungsarc updaten
    if (map.getLayer(layerIds.nodes.position.arc)) {
      map.setLayoutProperty(
        layerIds.nodes.position.arc,
        "icon-rotate",
        locationObject.heading
      );
    }

    if (locationObject.heading != null && cameraMoveToHeading) {
      map.panTo([locationObject.lng, locationObject.lat], {
        bearing: locationObject.heading,
      });
    }
  };
  const setAccuracyCircle = () => {
    if (
      !serverDataLoaded ||
      userAllowedGPS == null ||
      !mapLoaded ||
      locationObject.accuracy == null
    )
      return;
    // ausrechnen der Accuracy in metern
    map.setPaintProperty(layerIds.nodes.position.radar, "circle-radius", {
      stops: [
        [0, 0],
        [22, getMetersFromPixels(locationObject.accuracy, locationObject.lat)],
      ],
      base: 2,
    });
  };
  const setPositionData = () => {
    if (!serverDataLoaded || userAllowedGPS == null || !mapLoaded || isTerminal)
      return;

    if (currentRoute && currentRoute) {
      const leg = currentRoute.getCurrentLeg();

      if (leg.isIndoor) {
       // setPositionFeature();
      }
    }

    // Nutzer Position nicht erlaubt -> Position resetten
    /* if ((!userAllowedGPS || !locationObject.isSet) && canResetUserPosition) {
      setPositionFeature();
      return;
    } */
    if (locationObject.lat == null || locationObject.lng == null) {
      return;
    }

    pointCollection.current.features[0].geometry.coordinates = [
      locationObject.lng,
      locationObject.lat,
    ];

    // Wenn Laststeps leer -> current position als coordinates einsetzen
    // Funktion für das initiale GPS Signal!
    if (!lastStep.current.length) {
      lastStep.current = [locationObject.lng, locationObject.lat];
      setPositionFeature([locationObject.lng, locationObject.lat]);
      return;
    }
    animationLoopCounter.current = 0;

    // LineFeature mit altem und neuen Punkt für die Zwischenpunkt Berechnung in Turf erstellen
    const lineFeature = {
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates: [
          lastStep.current,
          [locationObject.lng, locationObject.lat],
        ],
      },
    };

    // Array Errechnung mit den zwischenliegenden Features
    const lineDistance = length(lineFeature);
    const pointsFromLine = [];

    for (let i = 0; i < lineDistance; i += lineDistance / animationTime) {
      // Die Segmentdistance wird als easeIn-EaseOut errechnet
      const segmentDistance =
        Math.sin((Math.PI / 2) * (i / lineDistance)) * lineDistance;
      const segment = along(lineFeature, segmentDistance);

      pointsFromLine.push(segment.geometry.coordinates);
    }

    routeCollection.current.features[0].geometry.coordinates = pointsFromLine;

    // // Aktuellen Step zum lastStep machen
    // lastStep.current = [locationObject.lng, locationObject.lat];

    // token erstellen, zum anhalten, wenn animate() eigenständig läuft
    animationTokens.current = v1();
    // token übergeben
    animate(animationTokens.current);
  };
  const changeFloorToUser = () => {
    if (!mapLoaded || !locationObject.isSet) {
      return;
    }

    // überprüfen, ob user überhaupt aufm campus ist!
    if (
      CAMPUS_COLLECTION.getCurrentCampus() &&
      CAMPUS_COLLECTION.getCurrentCampus().getPolygon() &&
      locationObject?.isSet
    ) {
      const userInsideCampus = inside(
        [locationObject.lng, locationObject.lat],
        CAMPUS_COLLECTION.getCurrentCampus().getPolygon()
      );
      if (
        userInsideCampus &&
        !(initZIndex.current && currentFeature) &&
        // wenn GPS empfangen wird macht es keinen das stockwerk anzupassen
        locationObject.positionType === UserLocation.position_types.indoor
      ) {
        setCurrentFloorIndex(locationObject.zIndex);
      }
    }

    // beim ersten zIndex vom Location Object bitte noch nichts machen
    // um aufgerufene POIs aus der URL beim initialen Lade der Page auf dem richtigen Stockwerk anzuzeigen,
    // erst wenn der User daraufhin seine Etage ändert, mitändern
    initZIndex.current = false;
  };
  const setDefaultNode = () => {
    return;
    if (
      !mapLoaded ||
      !serverDataLoaded ||
      !isTerminal ||
      defaultStartNode == null
    )
      return;

    let node = getNodeById(
      defaultStartNode,
      CAMPUS_COLLECTION.getAllCampusNodes()
    );

    if (node) {
      // todo: später einfach in das locationObject übertragen, weils ja viel zu retarded ist auf diese
      //  art und weise
      setPositionFeature([node.coord[1], node.coord[0]]);
      // das LocationObject muss wissen, auf welchem stockwerk der user gerade ist!
      const defaultNodeFloorLevel = node.coord[2];
      setLocationObjectZIndex(
        CAMPUS_COLLECTION.getIndexFromLevel(defaultNodeFloorLevel)
      );
      // etage auf das default ding anwenden!
      // setCurrentFloorNumber(defaultNodeFloorLevel);
      map.setLayoutProperty(layerIds.nodes.position.arc, "visibility", "none");
    }
  };

  useEffect(setArc, [
    serverDataLoaded,
    userAllowedGPS,
    mapLoaded,
    locationObject.heading,
  ]);

  useEffect(setAccuracyCircle, [
    serverDataLoaded,
    userAllowedGPS,
    mapLoaded,
    locationObject.accuracy,
  ]);

  useEffect(setPositionData, [
    serverDataLoaded,
    userAllowedGPS,
    mapLoaded,
    locationObject.lng,
    locationObject.lat,
    isTerminal,
  ]);

  // Checkt immer Die momentane GPS Situation ab und ruft ein notwendiges Toast auf!
  // Allgemein findet hier das Update der Position bei GPS/Bluetoothänderungen statt
  useEffect(() => {
    if (!mapLoaded) return;

    setPositionColor(true);
  }, [mapLoaded, locationObject.zIndex, currentFloorIndex]);

  useEffect(() => {
    changeFloorToUser();
  }, [locationObject.isSet, locationObject.zIndex, mapLoaded]);

  useEffect(() => {
    if (
      currentMainPage === pageNames.map &&
      !hasLatestPosition &&
      userAllowedGPS
    ) {
      addToast(
        new Toast(
          "wegGeoError",
          intl.formatMessage({ id: "app.noLocation" }),
          "warning"
        )
      );
    }
  }, [hasLatestPosition]);

  useEffect(() => {
    setDefaultNode();
  }, [isTerminal, defaultStartNode, serverDataLoaded, mapLoaded]);
}
